// Conversation List Component
// Following existing patterns from ActivityFeed

import React, { useCallback, useEffect, useState } from 'react';
import { ConversationListProps } from '../../../types/messaging';
import { MatchRequest } from '../../../types/swipe';
import { swipeService } from '../../../services/Swipe/swipeService';
import { messagingService } from '../../../services/Messaging/messagingService';
import { PremiumModal } from '../../Premium/PremiumModal';
import { NotificationList } from '../NotificationList/NotificationList';
import { useSubscriptionContext } from '../../../providers/SubscriptionProvider';
import styled from 'styled-components'; // Import styled for local toggle styling
import { theme } from '../../../styles/theme';
import {
  ConversationListContainer,
  ConversationListHeader,
  ConversationListTitle,
  ConversationSearch,
  SearchInput,
  ConversationFilter,
  MessagingFilterButton,
  ConversationItems,
  ConversationItem,
  ConversationAvatar,
  ConversationContent,
  ConversationHeader,
  ConversationName,
  ConversationTime,
  ConversationPreview,
  ConversationBadge,
  UnreadBadge,
  OnlineIndicator,
  ConversationListLoading,
  ConversationListError,
  EmptyConversationState,
  EmptyConversationIcon,
  EmptyConversationText,
  UpgradeButton
} from '../../../styles/Messaging';



const ViewToggle = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 12px;
  background: none;
  border: none;
  border-bottom: 2px solid ${props => props.$active ? '#ff8c42' : 'transparent'};
  color: ${props => props.$active ? '#ff8c42' : '#888'};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 1rem;

  &:hover {
    color: #ff8c42;
    background-color: rgba(255, 140, 66, 0.05);
  }
`;

const PendingBetSection = styled.div`
  border-bottom: 1px solid #eee;
`;

const PendingBetItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(255, 140, 66, 0.05);
  }
`;

const PendingBetIcon = styled.div`
  font-size: 1.25rem;
`;

const PendingBetText = styled.div`
  flex: 1;
  font-size: 0.9rem;
  color: #444;
`;

const PendingBetStatus = styled.div`
  font-size: 0.75rem;
  color: #888;
  margin-top: 2px;
`;

/**
 * The same "this one wants you" dot conversations carry.
 *
 * A bet arriving is a message arriving — it just lives in a different list —
 * so it had no indicator at all and a new bet looked identical to one you had
 * already dealt with.
 */
const PendingBetDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${theme.colors.primary};
  flex-shrink: 0;
  align-self: center;
`;

/**
 * "There is something here" for the Messages / Notifications tabs.
 *
 * A dot rather than a count: these two sit above the filters, which already
 * carry numbers, and stacking a second set of figures on top reads as a
 * dashboard rather than an inbox.
 */
const TabDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-left: 6px;
  border-radius: 50%;
  background: ${theme.colors.primary};
  vertical-align: middle;
`;

/** Count of bets waiting on you, shown on the Bets tab itself. */
const FilterCountBadge = styled.span`
  margin-left: 6px;
  display: inline-block;
  min-width: 16px;
  padding: 0 5px;
  border-radius: 10px;
  background: ${theme.colors.primary};
  color: #fff;
  font-size: 0.625rem;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  vertical-align: middle;
`;
export const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  currentConversation,
  currentUserId,
  onConversationSelect,
  onSearch,
  onFilter,
  onPendingBetSelect
}) => {
  // const [searchQuery, setSearchQuery] = useState(''); // Removed search
  const [activeFilter, setActiveFilter] = useState('all');
  const [isPremiumModalOpen, setIsPremiumModalOpen] = useState(false);
  const { isPremium } = useSubscriptionContext();
  const [viewMode, setViewMode] = useState<'messages' | 'notifications'>('messages');
  const [pendingBets, setPendingBets] = useState<MatchRequest[]>([]);

  // Unread notifications, for the dot on the Notifications tab. Fetched here
  // rather than inside NotificationList because the dot has to be visible from
  // the Messages side — a badge only rendered once you switch tabs cannot tell
  // you to switch tabs.
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  const loadUnreadNotifications = useCallback(async () => {
    try {
      const all = await messagingService.getNotifications();
      setUnreadNotificationCount(all.filter((n) => !n.isRead).length);
    } catch (error) {
      console.error('Failed to load notification count:', error);
    }
  }, []);

  useEffect(() => {
    loadUnreadNotifications();
  }, [loadUnreadNotifications]);

  // Re-read on returning to Messages: whatever was read in the other tab
  // should clear the dot.
  useEffect(() => {
    if (viewMode === 'messages') loadUnreadNotifications();
  }, [viewMode, loadUnreadNotifications]);

  const loadPendingBets = useCallback(async () => {
    try {
      const all = await swipeService.getBetRequests('all');
      setPendingBets(all.filter(r => r.status === 'PENDING' || r.status === 'COUNTER'));
    } catch (error) {
      console.error('Failed to load pending bet requests:', error);
    }
  }, []);

  // Loaded on mount, not only when the Bets tab is opened — the tab now
  // carries a count, and a badge that only appears once you have already
  // looked is no use for telling you to look.
  useEffect(() => {
    loadPendingBets();
  }, [loadPendingBets]);

  // Refreshed when the tab is opened too, so responding elsewhere is reflected.
  useEffect(() => {
    if (viewMode === 'messages' && activeFilter === 'bets') {
      loadPendingBets();
    }
  }, [viewMode, activeFilter, loadPendingBets]);

  // const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const query = e.target.value;
  //   setSearchQuery(query);
  //   onSearch(query);
  // };

  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter);
    onFilter(filter);
  };

  const filteredConversations = conversations.filter(conversation => {
    // const matchesSearch = searchQuery === '' ||
    //   conversation.participants.some(p =>
    //     p.name.toLowerCase().includes(searchQuery.toLowerCase())
    //   ) ||
    //   conversation.lastMessage?.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
    //   conversation.betMessage?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSearch = true; // Always true since search is removed

    // Unread means unread, bet or not. Excluding bets here meant someone whose
    // only waiting messages were bets opened the Unread tab and was told they
    // were all caught up.
    const matchesFilter = activeFilter === 'all' ||
      (activeFilter === 'unread' && conversation.unreadCount > 0) ||
      (activeFilter === 'bets' && conversation.isBet === true);

    return matchesSearch && matchesFilter;
  });


  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return date.toLocaleDateString();
  };

  // Whether the negotiation is currently waiting on *this* user to act.
  const isActionableByMe = (bet: MatchRequest) =>
    (bet.status === 'PENDING' && bet.receiverId === currentUserId) ||
    (bet.status === 'COUNTER' && bet.senderId === currentUserId);

  // Bets waiting on this user, for the tab badge.
  const actionableBetCount = pendingBets.filter(isActionableByMe).length;

  // Unread MESSAGES, not unread conversations: three messages in one thread is
  // three things you have not read, and the number people expect to see on an
  // inbox is the number of messages waiting.
  //
  // All and Unread necessarily show the same figure — Unread is a filtered
  // view of All, so anything counted in one is counted in the other. The badge
  // on All is what tells you there is something to find; the badge on Unread
  // is what tells you the filter is not empty before you try it.
  const unreadMessageCount = conversations.reduce(
    (total, conversation) => total + (conversation.unreadCount || 0),
    0,
  );

  const describePendingBet = (bet: MatchRequest) => {
    const otherName = bet.senderId === currentUserId
      ? (bet.receiverProfile?.name || 'Someone')
      : (bet.senderProfile?.name || 'Someone');
    const level = (bet.response.counterBet || bet.betLevel || '').toUpperCase();
    if (isActionableByMe(bet)) {
      return { text: `🎲 ${level} bet from ${otherName}`, status: 'Awaiting your response — tap to respond' };
    }
    return { text: `🎲 ${level} bet with ${otherName}`, status: 'Waiting on them to respond' };
  };

  return (
    <ConversationListContainer>
      <ConversationListHeader>
        <div style={{ display: 'flex', borderBottom: '1px solid #eee', marginBottom: '10px' }}>
          <ViewToggle $active={viewMode === 'messages'} onClick={() => setViewMode('messages')}>
            Messages
            {(unreadMessageCount > 0 || actionableBetCount > 0) && (
              <TabDot aria-label="You have new messages" />
            )}
          </ViewToggle>
          <ViewToggle $active={viewMode === 'notifications'} onClick={() => setViewMode('notifications')}>
            Notifications
            {unreadNotificationCount > 0 && (
              <TabDot aria-label="You have new notifications" />
            )}
          </ViewToggle>
        </div>

        {viewMode === 'messages' && (
          <ConversationFilter>
            <MessagingFilterButton
              $active={activeFilter === 'all'}
              onClick={() => handleFilterChange('all')}
            >
              All
              {unreadMessageCount > 0 && (
                <FilterCountBadge aria-label={`${unreadMessageCount} unread messages`}>
                  {unreadMessageCount}
                </FilterCountBadge>
              )}
            </MessagingFilterButton>
            <MessagingFilterButton
              $active={activeFilter === 'unread'}
              onClick={() => handleFilterChange('unread')}
            >
              Unread
              {unreadMessageCount > 0 && (
                <FilterCountBadge aria-label={`${unreadMessageCount} unread messages`}>
                  {unreadMessageCount}
                </FilterCountBadge>
              )}
            </MessagingFilterButton>
            <MessagingFilterButton
              $active={activeFilter === 'bets'}
              onClick={() => handleFilterChange('bets')}
            >
              Bets
              {/* A bet arriving is worth knowing about from the Messages tab.
                  Without this the only way to discover one was to go looking
                  in Bets, which is precisely what a notification is for. */}
              {actionableBetCount > 0 && (
                <FilterCountBadge aria-label={`${actionableBetCount} bets awaiting your response`}>
                  {actionableBetCount}
                </FilterCountBadge>
              )}
            </MessagingFilterButton>
          </ConversationFilter>
        )}
      </ConversationListHeader>

      {viewMode === 'notifications' ? (
        <NotificationList />
      ) : (
        <ConversationItems>
          {activeFilter === 'bets' && pendingBets.length > 0 && (
            <PendingBetSection>
              {pendingBets.map((bet) => {
                const { text, status } = describePendingBet(bet);
                return (
                  <PendingBetItem
                    key={bet.id}
                    onClick={() => onPendingBetSelect?.(bet)}
                  >
                    <PendingBetIcon>{isActionableByMe(bet) ? '🔥' : '⏳'}</PendingBetIcon>
                    <PendingBetText>
                      {text}
                      <PendingBetStatus>{status}</PendingBetStatus>
                    </PendingBetText>
                    {/* Only on bets waiting for YOU. One you have sent is not
                        new to you, and dotting it would train people to ignore
                        the dot. */}
                    {isActionableByMe(bet) && <PendingBetDot aria-label="Awaiting your response" />}
                  </PendingBetItem>
                );
              })}
            </PendingBetSection>
          )}
          {activeFilter === 'unread' && filteredConversations.length === 0 ? (
            // This is the Unread filter's empty state, and it used to sell
            // Premium to everyone who reached it — including subscribers, who
            // were being told to upgrade to something they already pay for.
            // Having no unread messages is not a paywall.
            <EmptyConversationState>
              <EmptyConversationIcon>{isPremium ? '📭' : '👑'}</EmptyConversationIcon>
              <EmptyConversationText>
                {isPremium
                  ? 'Nothing unread. You are all caught up.'
                  : 'Message Bets are free for everyone. Premium adds messaging outside of bets.'}
              </EmptyConversationText>
              {!isPremium && (
                <UpgradeButton onClick={() => setIsPremiumModalOpen(true)}>
                  Upgrade to Premium
                </UpgradeButton>
              )}
            </EmptyConversationState>
          ) : filteredConversations.length === 0 && !(activeFilter === 'bets' && pendingBets.length > 0) ? (
            <EmptyConversationState>
              <EmptyConversationIcon>💬</EmptyConversationIcon>
              <EmptyConversationText>
                {activeFilter === 'bets' ? 'No bet offers yet' : 'No conversations yet'}
              </EmptyConversationText>
            </EmptyConversationState>
          ) : (
            filteredConversations.map((conversation) => {
              const otherParticipant = conversation.participants.find(p => p.id !== currentUserId);
              const isActive = conversation.id === currentConversation?.id;

              return (
                <ConversationItem
                  key={conversation.id}
                  $isActive={isActive}
                  onClick={() => onConversationSelect(conversation)}
                >
                  <ConversationAvatar>
                    {otherParticipant?.avatar ? (
                      <img
                        src={otherParticipant.avatar}
                        alt={otherParticipant.name}
                        style={{ width: '100%', height: '100%', borderRadius: '50%' }}
                      />
                    ) : (
                      getInitials(otherParticipant?.name || 'U')
                    )}
                  </ConversationAvatar>

                  <ConversationContent>
                    <ConversationHeader>
                      <ConversationName>{otherParticipant?.name || 'Unknown User'}</ConversationName>
                      <ConversationTime>
                        {conversation.lastMessage ? formatTime(conversation.lastMessage.createdAt) : ''}
                      </ConversationTime>
                    </ConversationHeader>

                    <ConversationPreview>
                      {conversation.isBet && conversation.betMessage
                        ? `🎲 ${conversation.betLevel?.toUpperCase()} Bet: ${conversation.betMessage}`
                        : conversation.contentLocked && conversation.lastMessage
                          // Someone wrote to them — that much is worth knowing,
                          // and is the reason to subscribe. What was said is
                          // not in the payload to show even if we wanted to.
                          ? '🔒 New message — upgrade to read'
                          : conversation.lastMessage?.content || 'No messages yet'}
                    </ConversationPreview>
                  </ConversationContent>

                  <ConversationBadge>
                    {conversation.unreadCount > 0 && (
                      <UnreadBadge>{conversation.unreadCount}</UnreadBadge>
                    )}
                    <OnlineIndicator $isOnline={otherParticipant?.isOnline || false} />
                  </ConversationBadge>
                </ConversationItem>
              );
            })
          )}
        </ConversationItems>
      )}

      <PremiumModal
        isOpen={isPremiumModalOpen}
        onClose={() => setIsPremiumModalOpen(false)}
      />
    </ConversationListContainer>
  );
};

ConversationList.displayName = 'ConversationList';
